import { normaliseIndianMobile } from "./phone";

/**
 * Validation for the public "request a login" form — pure, so it is tested
 * without a browser or a database.
 */
export const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh",
  "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal",
] as const;

export const HEARD_FROM = ["Advertisement", "A Sportsun salesman", "Another dealer", "Search / website", "Other"] as const;

export type LoginRequestInput = {
  businessName: string;
  ownerName: string;
  phone: string;
  city: string;
  state: string;
  gstin?: string | null;
  heardFrom?: string | null;
  note?: string | null;
  /** honeypot — must be empty */
  website?: string | null;
};

export type Validated = {
  businessName: string;
  ownerName: string;
  phone: string;
  city: string;
  state: string;
  gstin: string | null;
  heardFrom: string | null;
  note: string | null;
};

const GSTIN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export function validateLoginRequest(input: LoginRequestInput): { ok: true; value: Validated } | { ok: false; error: string } {
  const s = (v: string | null | undefined, max = 120) => (v ?? "").trim().slice(0, max);
  if (s(input.website)) return { ok: false, error: "Could not submit." }; // a bot filled the hidden field
  const businessName = s(input.businessName);
  const ownerName = s(input.ownerName);
  const city = s(input.city, 60);
  const state = s(input.state, 60);
  const phone = normaliseIndianMobile(input.phone);
  if (businessName.length < 2) return { ok: false, error: "Enter the shop or business name." };
  if (ownerName.length < 2) return { ok: false, error: "Enter the owner's name." };
  if (!phone) return { ok: false, error: "Enter a 10-digit Indian mobile number." };
  if (!city) return { ok: false, error: "Enter the city." };
  if (!(INDIAN_STATES as readonly string[]).includes(state)) return { ok: false, error: "Pick the state." };
  const gstinRaw = s(input.gstin, 20).toUpperCase().replace(/\s+/g, "");
  if (gstinRaw && !GSTIN.test(gstinRaw)) return { ok: false, error: "That GSTIN does not look right (15 characters, e.g. 07ABCDE1234F1Z5). Leave it blank if unsure." };
  const heard = s(input.heardFrom, 60);
  return {
    ok: true,
    value: {
      businessName,
      ownerName,
      phone,
      city,
      state,
      gstin: gstinRaw || null,
      heardFrom: heard || null,
      note: s(input.note, 1000) || null,
    },
  };
}

export const REQUEST_STATUSES = ["NEW", "CALLED", "CREATED", "DECLINED"] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  NEW: "New",
  CALLED: "Called",
  CREATED: "Created in Notion",
  DECLINED: "Declined",
};
